// frontend/src/pages/groups/GroupDetailsPage.jsx
import React, { useEffect, useState } from 'react';
import { getGroupById, requestJoinGroup } from '../../services/groupService';
import { checkMembership, checkMembershipLocally } from '../../services/membershipService';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Loader from '../../components/common/Loader';
import Button from '../../components/common/Button';
import { 
  CalendarIcon, 
  UsersIcon, 
  CurrencyRupeeIcon,
  MapPinIcon,
  ClockIcon,
  FlagIcon,
  TagIcon,
  StarIcon,
  SparklesIcon,
  HomeIcon,
  TruckIcon,
  FireIcon,
  HeartIcon,
  ShieldCheckIcon,
  LockOpenIcon,
  ChartBarIcon,
  CakeIcon,
  UserGroupIcon,
  SunIcon,
  MoonIcon,
  AcademicCapIcon,
  CameraIcon,
  UserCircleIcon,
  ChevronLeftIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  BellIcon,
  ArrowRightIcon,
  CheckBadgeIcon,
  XMarkIcon,
  PlusIcon,
  ArrowLeftCircleIcon
} from '@heroicons/react/24/outline';
import { 
  RocketLaunchIcon,
  UserPlusIcon,
  ViewfinderCircleIcon,
  CurrencyDollarIcon,
  UserMinusIcon
} from '@heroicons/react/24/solid';

const GroupDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [membership, setMembership] = useState({
    isCreator: false,
    isMember: false,
    hasPendingRequest: false,
    userRole: null
  });

  useEffect(() => {
    if (!id || id === 'undefined') {
      setError('Invalid group ID');
      setLoading(false);
      toast.error('Group not found');
      navigate('/groups');
      return;
    }

    const fetchGroupAndMembership = async () => {
      try {
        setLoading(true);
        
        // First get group details
        const groupData = await getGroupById(id);
        setGroup(groupData);
        
        // Then check membership status
        try {
          const membershipData = await checkMembership(id);
          console.log('✅ Membership API result:', membershipData);
          setMembership(membershipData);
        } catch (membershipError) {
          console.log('⚠️ Membership API failed, falling back to local check');
          // Fallback to local check
          const localMembership = checkMembershipLocally(groupData, user);
          setMembership(localMembership);
        }
        
      } catch (err) {
        console.error('Error fetching group:', err);
        setError('Failed to load group details');
        toast.error(err.response?.data?.message || 'Group not found');
        navigate('/groups');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGroupAndMembership();
  }, [id, navigate, user]);

  const handleJoin = async () => {
    if (!id || !group) return;
    
    try {
      setRequesting(true);
      const res = await requestJoinGroup(id, `I'd like to join your trip to ${group.destination}`);
      toast.success(res.message || 'Join request sent!');
      
      // Refresh membership status
      const membershipData = await checkMembership(id);
      setMembership(membershipData);
      
      // Refresh group data to update join requests
      const updatedGroup = await getGroupById(id);
      setGroup(updatedGroup);
      
    } catch (err) {
      console.error('Join error:', err);
      toast.error(err.response?.data?.message || 'Failed to send join request');
    } finally {
      setRequesting(false);
    }
  };

  // Handle click on user profile
  const handleUserClick = (userId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (userId) {
      navigate(`/user/${userId}`);
    }
  };

  // Calculate enhanced group data
  const getEnhancedGroupData = () => {
    if (!group) return null;
    
    const startDate = new Date(group.startDate);
    const endDate = new Date(group.endDate);
    const today = new Date();
    
    // Calculate days remaining
    const diffTime = startDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Calculate trip duration
    const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // Get travel style icons
    const travelStyleIcons = {
      'adventure': { icon: <FireIcon className="h-5 w-5" />, color: 'text-red-500 bg-red-50' },
      'luxury': { icon: <SparklesIcon className="h-5 w-5" />, color: 'text-yellow-500 bg-yellow-50' },
      'budget': { icon: <CurrencyRupeeIcon className="h-5 w-5" />, color: 'text-green-500 bg-green-50' },
      'backpacking': { icon: <AcademicCapIcon className="h-5 w-5" />, color: 'text-blue-500 bg-blue-50' },
      'solo': { icon: <UserGroupIcon className="h-5 w-5" />, color: 'text-purple-500 bg-purple-50' },
      'family': { icon: <HomeIcon className="h-5 w-5" />, color: 'text-pink-500 bg-pink-50' },
      'digital_nomad': { icon: <SunIcon className="h-5 w-5" />, color: 'text-indigo-500 bg-indigo-50' },
      'slow_travel': { icon: <ClockIcon className="h-5 w-5" />, color: 'text-teal-500 bg-teal-50' }
    };

    // Calculate current members count
    const currentMembersCount = group.currentMembers?.filter(m => 
      m && m.status === 'approved'
    ).length || 0;
    
    const isFull = currentMembersCount >= (group.maxMembers || 0);

    return {
      ...group,
      daysRemaining: diffDays,
      tripDuration: duration,
      travelStyleIcons,
      currentMembersCount,
      isFull,
      isTripActive: diffDays <= 0 && group.status === 'confirmed',
      isTripCompleted: group.status === 'completed',
      isTripCancelled: group.status === 'cancelled',
      availableSlots: (group.maxMembers || 0) - currentMembersCount
    };
  };

  const enhancedGroup = getEnhancedGroupData();

  // Debug log to check membership status
  useEffect(() => {
    if (membership.isCreator) {
      console.log('🎯 User is CREATOR - Dashboard should be accessible');
    }
    if (membership.isMember) {
      console.log('✅ User is MEMBER - Dashboard should be accessible');
    }
  }, [membership]);

  // New floating action buttons component
  const FloatingActionButtons = () => {
    if (!enhancedGroup) return null;

    const getMainAction = () => {
      if (membership.isCreator || membership.isMember) {
        return {
          label: 'Dashboard',
          icon: <ViewfinderCircleIcon className="h-6 w-6" />,
          color: 'from-indigo-500 to-purple-600',
          hover: 'from-indigo-600 to-purple-700',
          action: () => navigate(`/groups/${id}/dashboard`),
          isPrimary: true
        };
      }
      
      if (membership.hasPendingRequest) {
        return {
          label: 'Pending',
          icon: <ClockIcon className="h-6 w-6" />,
          color: 'from-yellow-500 to-orange-500',
          hover: 'from-yellow-600 to-orange-600',
          action: null,
          isPrimary: true,
          disabled: true
        };
      }
      
      if (enhancedGroup.isFull && !membership.isCreator && !membership.isMember) {
        return {
          label: 'Group Full',
          icon: <XMarkIcon className="h-6 w-6" />,
          color: 'from-red-500 to-pink-600',
          hover: 'from-red-600 to-pink-700',
          action: null,
          isPrimary: true,
          disabled: true
        };
      }
      
      if (!membership.isCreator && !membership.isMember && !membership.hasPendingRequest) {
        return {
          label: 'Join Trip',
          icon: <RocketLaunchIcon className="h-6 w-6" />,
          color: 'from-blue-500 to-purple-600',
          hover: 'from-blue-600 to-purple-700',
          action: handleJoin,
          isPrimary: true,
          loading: requesting
        };
      }
      
      return null;
    };

    const actionButtons = [];

    // For Creator
    if (membership.isCreator) {
      actionButtons.push(
        {
          label: 'Manage',
          icon: <Cog6ToothIcon className="h-5 w-5" />,
          color: 'bg-gradient-to-r from-yellow-500 to-orange-500',
          action: () => navigate('/group-requests'),
          tooltip: 'Manage Requests'
        },
        {
          label: 'Chat',
          icon: <ChatBubbleLeftRightIcon className="h-5 w-5" />,
          color: 'bg-gradient-to-r from-green-500 to-emerald-600',
          action: () => navigate(`/groups/${id}/chat`),
          tooltip: 'Group Chat'
        },
        {
          label: 'Expenses',
          icon: <CurrencyDollarIcon className="h-5 w-5" />,
          color: 'bg-gradient-to-r from-blue-500 to-indigo-600',
          action: () => navigate(`/groups/${id}/expenses`),
          tooltip: 'Manage Expenses'
        }
      );
    }

    // For Members (not creator)
    if (membership.isMember && !membership.isCreator) {
      actionButtons.push(
        {
          label: 'Chat',
          icon: <ChatBubbleLeftRightIcon className="h-5 w-5" />,
          color: 'bg-gradient-to-r from-green-500 to-emerald-600',
          action: () => navigate(`/groups/${id}/chat`),
          tooltip: 'Group Chat'
        },
        {
          label: 'Expenses',
          icon: <CurrencyDollarIcon className="h-5 w-5" />,
          color: 'bg-gradient-to-r from-blue-500 to-indigo-600',
          action: () => navigate(`/groups/${id}/expenses`),
          tooltip: 'Manage Expenses'
        }
      );
    }

    // Common action for all
    actionButtons.push({
      label: 'Back',
      icon: <ArrowLeftCircleIcon className="h-5 w-5" />,
      color: 'bg-gradient-to-r from-gray-500 to-gray-700',
      action: () => navigate('/groups'),
      tooltip: 'Explore More Trips'
    });

    const mainAction = getMainAction();

    return (
      <>
        {/* Desktop Floating Action Panel */}
        <div className="hidden lg:block">
          <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-40">
            <div className="flex flex-col items-end space-y-4">
              {/* Main Action Button */}
              {mainAction && (
                <div className="relative group">
                  <button
                    onClick={mainAction.action}
                    disabled={mainAction.disabled || mainAction.loading}
                    className={`
                      relative flex items-center space-x-3 px-6 py-4 rounded-2xl shadow-2xl
                      bg-gradient-to-r ${mainAction.color} hover:${mainAction.hover}
                      text-white font-bold text-lg transform transition-all duration-300
                      hover:scale-105 hover:shadow-3xl active:scale-95
                      ${(mainAction.disabled || mainAction.loading) ? 'opacity-70 cursor-not-allowed' : ''}
                      ${mainAction.isPrimary ? 'animate-pulse-slow' : ''}
                    `}
                  >
                    <div className="relative">
                      {mainAction.icon}
                      {mainAction.loading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    <span className="whitespace-nowrap">{mainAction.label}</span>
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-white/20 to-transparent rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                  </button>
                  <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                    <div className="bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap">
                      {mainAction.disabled ? 'Action unavailable' : 'Click to proceed'}
                    </div>
                    <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                      <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Secondary Action Buttons */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-2xl p-4 border border-gray-100">
                <div className="space-y-3">
                  {actionButtons.map((btn, idx) => (
                    <div key={idx} className="relative group">
                      <button
                        onClick={btn.action}
                        className={`
                          flex items-center justify-center w-14 h-14 rounded-xl
                          ${btn.color} text-white shadow-lg
                          transform transition-all duration-300
                          hover:scale-110 hover:shadow-xl active:scale-95
                        `}
                        title={btn.tooltip}
                      >
                        {btn.icon}
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-white/30 to-transparent rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                      </button>
                      <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                        <div className="bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap">
                          {btn.tooltip}
                        </div>
                        <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                          <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Action Bar */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
          {/* Main Action Button for Mobile */}
          {mainAction && (
            <div className="px-4 pb-4">
              <button
                onClick={mainAction.action}
                disabled={mainAction.disabled || mainAction.loading}
                className={`
                  w-full flex items-center justify-center space-x-3 px-6 py-4 rounded-2xl shadow-2xl
                  bg-gradient-to-r ${mainAction.color} hover:${mainAction.hover}
                  text-white font-bold text-lg transform transition-all duration-300
                  active:scale-95
                  ${(mainAction.disabled || mainAction.loading) ? 'opacity-70 cursor-not-allowed' : ''}
                  ${mainAction.isPrimary ? 'animate-pulse-slow' : ''}
                `}
              >
                <div className="relative">
                  {mainAction.icon}
                  {mainAction.loading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <span>{mainAction.label}</span>
              </button>
            </div>
          )}

          {/* Secondary Actions Bar */}
          <div className="bg-white/95 backdrop-blur-lg border-t border-gray-200 shadow-2xl">
            <div className="flex justify-around items-center px-4 py-3">
              {actionButtons.map((btn, idx) => (
                <button
                  key={idx}
                  onClick={btn.action}
                  className="flex flex-col items-center justify-center p-3 rounded-xl active:bg-gray-100 transition-colors"
                >
                  <div className={`${btn.color} w-12 h-12 rounded-xl flex items-center justify-center mb-1`}>
                    {btn.icon}
                  </div>
                  <span className="text-xs font-medium text-gray-700">{btn.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Menu Toggle Button (Alternative Design) */}
        <div className="lg:hidden fixed bottom-24 right-4 z-50">
          <button
            onClick={() => setShowActionMenu(!showActionMenu)}
            className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 shadow-2xl flex items-center justify-center text-white transform transition-all duration-300 hover:scale-110 active:scale-95"
          >
            {showActionMenu ? (
              <XMarkIcon className="h-7 w-7" />
            ) : (
              <PlusIcon className="h-7 w-7" />
            )}
          </button>

          {/* Mobile Action Menu */}
          {showActionMenu && (
            <div className="absolute bottom-full right-0 mb-4 bg-white rounded-2xl shadow-2xl p-4 border border-gray-100 min-w-[200px] animate-slide-up">
              <div className="space-y-2">
                {actionButtons.map((btn, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      btn.action();
                      setShowActionMenu(false);
                    }}
                    className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <div className={`${btn.color} w-10 h-10 rounded-lg flex items-center justify-center`}>
                      {btn.icon}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-800">{btn.label}</div>
                      <div className="text-xs text-gray-500">{btn.tooltip}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh]">
        <Loader size="lg" />
        <span className="mt-4 text-gray-600 text-lg">Loading amazing trip details...</span>
      </div>
    );
  }

  if (error || !enhancedGroup) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 mx-auto mb-4 text-gray-400">
          <FlagIcon className="w-full h-full" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{error || 'Group not found'}</h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          The travel group you're looking for might have completed its journey or been removed.
        </p>
        <button 
          onClick={() => navigate('/groups')}
          className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300"
        >
          Discover Other Trips
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Add CSS animations */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .shadow-3xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 py-6 pb-24 lg:pb-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ChevronLeftIcon className="w-5 h-5" />
          <span className="hidden sm:inline">Back to Trips</span>
        </button>

        {/* Main Header with Gradient */}
        <div className="relative rounded-2xl overflow-hidden mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-90"></div>
          <div className="relative z-10 p-6 md:p-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-6 md:mb-0">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    enhancedGroup.status === 'planning' ? 'bg-yellow-500 text-white' :
                    enhancedGroup.status === 'confirmed' ? 'bg-green-500 text-white' :
                    enhancedGroup.status === 'active' ? 'bg-blue-500 text-white' :
                    enhancedGroup.status === 'completed' ? 'bg-gray-600 text-white' :
                    'bg-red-500 text-white'
                  }`}>
                    {enhancedGroup.status?.toUpperCase()}
                  </div>
                  <div className="flex items-center space-x-2">
                    <LockOpenIcon className="h-5 w-5" />
                    <span className="text-sm">{enhancedGroup.privacy === 'public' ? 'Public Group' : 'Private Group'}</span>
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3">{enhancedGroup.destination}</h1>
                <p className="text-lg md:text-xl opacity-90 mb-6 max-w-3xl">{enhancedGroup.description}</p>
                
                {/* Membership Badge */}
                <div className="flex flex-wrap items-center gap-3">
                  {membership.isCreator && (
                    <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                      <div className="w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                        <CrownIcon className="h-3 w-3 text-white" />
                      </div>
                      <span className="font-medium">You are the Creator</span>
                    </div>
                  )}
                  {membership.isMember && !membership.isCreator && (
                    <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                      <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                        <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="font-medium">You are a Member</span>
                    </div>
                  )}
                  {membership.hasPendingRequest && (
                    <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                      <ClockIcon className="h-5 w-5 text-yellow-300" />
                      <span className="font-medium">Join Request Pending</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 md:p-6 mt-4 md:mt-0">
                <div className="text-center">
                  <div className={`text-2xl md:text-3xl font-bold mb-1 ${
                    enhancedGroup.daysRemaining < 0 
                      ? 'text-red-300' 
                      : enhancedGroup.daysRemaining < 7 
                      ? 'text-yellow-300' 
                      : 'text-green-300'
                  }`}>
                    {enhancedGroup.daysRemaining < 0 
                      ? 'Trip Completed' 
                      : enhancedGroup.daysRemaining === 0 
                      ? 'Today!' 
                      : enhancedGroup.daysRemaining}
                  </div>
                  <div className="text-sm opacity-80">
                    {enhancedGroup.daysRemaining >= 0 
                      ? enhancedGroup.daysRemaining === 1 
                        ? 'day to go' 
                        : 'days to go'
                      : 'days ago'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Left Column - Trip Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Trip Information Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <MapPinIcon className="h-6 w-6 mr-2 text-blue-500" />
                Trip Details
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Route Information */}
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <FlagIcon className="h-5 w-5 mr-2 text-green-500" />
                      Route
                    </h3>
                    <div className="space-y-2 pl-7">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                        <div>
                          <p className="font-medium">From: {enhancedGroup.startingLocation?.address}</p>
                          <p className="text-sm text-gray-500">Start Location</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                        <div>
                          <p className="font-medium">To: {enhancedGroup.destinationLocation?.address}</p>
                          <p className="text-sm text-gray-500">Destination</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <CalendarIcon className="h-5 w-5 mr-2 text-purple-500" />
                      Trip Dates
                    </h3>
                    <div className="space-y-2 pl-7">
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                        <div>
                          <p className="font-medium">Start Date</p>
                          <p className="text-sm text-gray-600">{new Date(enhancedGroup.startDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                        <div>
                          <p className="font-medium">End Date</p>
                          <p className="text-sm text-gray-600">{new Date(enhancedGroup.endDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                      </div>
                      <div className="text-center p-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg">
                        <span className="font-semibold text-purple-700">{enhancedGroup.tripDuration} days total</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Budget & Capacity */}
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <CurrencyRupeeIcon className="h-5 w-5 mr-2 text-green-500" />
                      Budget Range
                    </h3>
                    <div className="pl-7">
                      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                        <div className="flex flex-col sm:flex-row sm:justify-between items-center mb-2 space-y-2 sm:space-y-0">
                          <span className="text-2xl font-bold text-green-700">
                            ₹{enhancedGroup.budget?.min?.toLocaleString()}
                          </span>
                          <span className="text-gray-500">to</span>
                          <span className="text-2xl font-bold text-green-700">
                            ₹{enhancedGroup.budget?.max?.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full"
                            style={{ width: '70%' }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">Per person • {enhancedGroup.budget?.currency || 'INR'}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <UsersIcon className="h-5 w-5 mr-2 text-blue-500" />
                      Group Capacity
                    </h3>
                    <div className="pl-7">
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                        <div className="flex justify-between items-center mb-3">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-blue-600">{enhancedGroup.currentMembersCount}</div>
                            <div className="text-sm text-gray-600">Current</div>
                          </div>
                          <div className="text-4xl text-gray-300">/</div>
                          <div className="text-center">
                            <div className="text-3xl font-bold text-blue-600">{enhancedGroup.maxMembers}</div>
                            <div className="text-sm text-gray-600">Maximum</div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full ${enhancedGroup.isFull ? 'bg-red-500' : 'bg-gradient-to-r from-blue-400 to-cyan-500'}`}
                            style={{ width: `${(enhancedGroup.currentMembersCount / enhancedGroup.maxMembers) * 100}%` }}
                          ></div>
                        </div>
                        <p className={`text-sm font-medium mt-2 ${enhancedGroup.isFull ? 'text-red-600' : 'text-green-600'}`}>
                          {enhancedGroup.isFull ? 'Group is Full' : `${enhancedGroup.availableSlots} spots available`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Travel Preferences Card */}
            {enhancedGroup.travelPreferences && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <SparklesIcon className="h-6 w-6 mr-2 text-pink-500" />
                  Travel Preferences
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Travel Style */}
                  {enhancedGroup.travelPreferences.travelStyle && enhancedGroup.travelPreferences.travelStyle.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Travel Style</h3>
                      <div className="flex flex-wrap gap-2">
                        {enhancedGroup.travelPreferences.travelStyle.map((style, index) => {
                          const styleConfig = enhancedGroup.travelStyleIcons[style];
                          return (
                            <span 
                              key={index}
                              className={`flex items-center px-3 py-2 rounded-lg ${styleConfig?.color || 'bg-gray-100 text-gray-700'}`}
                            >
                              {styleConfig?.icon || <TagIcon className="h-4 w-4 mr-2" />}
                              <span className="capitalize">{style.replace('_', ' ')}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Accommodation */}
                  {enhancedGroup.travelPreferences.accommodationType && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Accommodation</h3>
                      <div className="flex items-center p-3 bg-indigo-50 rounded-xl">
                        <HomeIcon className="h-5 w-5 mr-2 text-indigo-500" />
                        <span className="font-medium capitalize">{enhancedGroup.travelPreferences.accommodationType.replace('_', ' ')}</span>
                      </div>
                    </div>
                  )}

                  {/* Transportation */}
                  {enhancedGroup.travelPreferences.transportMode && enhancedGroup.travelPreferences.transportMode.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Transportation</h3>
                      <div className="flex flex-wrap gap-2">
                        {enhancedGroup.travelPreferences.transportMode.map((mode, index) => (
                          <span 
                            key={index}
                            className="flex items-center px-3 py-2 bg-amber-50 text-amber-700 rounded-lg"
                          >
                            <TruckIcon className="h-4 w-4 mr-2" />
                            <span className="capitalize">{mode}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lifestyle Preferences */}
                  <div className="md:col-span-2">
                    <h3 className="font-semibold text-gray-700 mb-3">Lifestyle Preferences</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {enhancedGroup.travelPreferences.smokingAllowed && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center mb-1">
                            <FireIcon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="text-sm font-medium">Smoking</span>
                          </div>
                          <span className="text-xs text-gray-600 capitalize">{enhancedGroup.travelPreferences.smokingAllowed}</span>
                        </div>
                      )}
                      
                      {enhancedGroup.travelPreferences.drinkingAllowed && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center mb-1">
                            <SunIcon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="text-sm font-medium">Drinking</span>
                          </div>
                          <span className="text-xs text-gray-600 capitalize">{enhancedGroup.travelPreferences.drinkingAllowed}</span>
                        </div>
                      )}
                      
                      {enhancedGroup.travelPreferences.petsAllowed && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center mb-1">
                            <HeartIcon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="text-sm font-medium">Pets</span>
                          </div>
                          <span className="text-xs text-gray-600 capitalize">{enhancedGroup.travelPreferences.petsAllowed}</span>
                        </div>
                      )}
                      
                      {enhancedGroup.travelPreferences.genderPreference && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center mb-1">
                            <UserGroupIcon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="text-sm font-medium">Gender</span>
                          </div>
                          <span className="text-xs text-gray-600 capitalize">{enhancedGroup.travelPreferences.genderPreference}</span>
                        </div>
                      )}
                      
                      {enhancedGroup.travelPreferences.ageRange && (
                        <div className="p-3 bg-gray-50 rounded-lg md:col-span-2">
                          <div className="flex items-center mb-1">
                            <CakeIcon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="text-sm font-medium">Age Range</span>
                          </div>
                          <span className="text-xs text-gray-600">
                            {enhancedGroup.travelPreferences.ageRange.min} - {enhancedGroup.travelPreferences.ageRange.max} years
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Group Members Card - Updated with clickable profiles */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <UserGroupIcon className="h-6 w-6 mr-2 text-green-500" />
                Travel Companions
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {enhancedGroup.currentMembers?.filter(m => m.status === 'approved').slice(0, 6).map((member, index) => (
                    <div 
                      key={index} 
                      className="p-4 border border-gray-100 rounded-xl hover:shadow-md transition-shadow cursor-pointer hover:bg-gray-50 active:scale-[0.98] transition-transform"
                      onClick={(e) => handleUserClick(member.user?._id || member.user?.id, e)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {member.user?.name?.charAt(0) || 'U'}
                          </div>
                          {member.role === 'creator' && (
                            <div className="absolute -top-1 -right-1 bg-yellow-500 text-white p-1 rounded-full">
                              <CrownIcon className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">
                            {member.user?.name || 'Travel Buddy'}
                            {member.role === 'creator' && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Creator</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600">Joined {new Date(member.joinedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {enhancedGroup.currentMembersCount > 6 && (
                  <div className="text-center pt-4">
                    <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg">
                      <UsersIcon className="h-5 w-5 text-gray-500" />
                      <span className="text-gray-700">
                        +{enhancedGroup.currentMembersCount - 6} more travel companions
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Additional Info */}
          <div className="space-y-8">
            {/* Group Info Card with clickable creator */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <ShieldCheckIcon className="h-6 w-6 mr-2 text-blue-500" />
                Group Information
              </h2>
              
              <div className="space-y-4">
                {/* Creator Profile Section - Clickable */}
                <div 
                  className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-all active:scale-[0.98]"
                  onClick={(e) => handleUserClick(enhancedGroup.createdBy?._id || enhancedGroup.createdBy?.id, e)}
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                      {enhancedGroup.createdBy?.name?.charAt(0) || 'C'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Group Leader</p>
                      <p className="text-gray-700">{enhancedGroup.createdBy?.name || 'Unknown'}</p>
                      <div className="flex items-center mt-1">
                        <UserCircleIcon className="h-4 w-4 mr-1 text-gray-500" />
                        <span className="text-xs text-gray-600">Click to view profile →</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Group Type */}
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-700">Group Type</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      enhancedGroup.groupType === 'open' ? 'bg-green-100 text-green-800' :
                      enhancedGroup.groupType === 'verified' ? 'bg-blue-100 text-blue-800' :
                      enhancedGroup.groupType === 'invite' ? 'bg-purple-100 text-purple-800' :
                      enhancedGroup.groupType === 'friends' ? 'bg-pink-100 text-pink-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {enhancedGroup.groupType?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {enhancedGroup.groupType === 'open' && 'Open for anyone to join'}
                    {enhancedGroup.groupType === 'verified' && 'Verified members only'}
                    {enhancedGroup.groupType === 'invite' && 'Invitation only'}
                    {enhancedGroup.groupType === 'friends' && 'Friends circle'}
                    {enhancedGroup.groupType === 'anonymous' && 'Anonymous travel group'}
                  </p>
                </div>

                <div className="space-y-3">
                  <InfoItem 
                    icon={<CalendarIcon className="h-5 w-5 text-gray-500" />}
                    label="Created On"
                    value={new Date(enhancedGroup.createdAt).toLocaleDateString()}
                  />
                  
                  <InfoItem 
                    icon={<UsersIcon className="h-5 w-5 text-gray-500" />}
                    label="Available Spots"
                    value={enhancedGroup.availableSlots}
                    highlight={true}
                  />
                  
                  <InfoItem 
                    icon={<ChartBarIcon className="h-5 w-5 text-gray-500" />}
                    label="Group Status"
                    value={enhancedGroup.status}
                    badge={true}
                  />
                  
                  {enhancedGroup.distanceKm > 0 && (
                    <InfoItem 
                      icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
                      label="Total Distance"
                      value={`${enhancedGroup.distanceKm} km`}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Tags & Interests */}
            {enhancedGroup.interests && enhancedGroup.interests.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <TagIcon className="h-6 w-6 mr-2 text-purple-500" />
                  Trip Interests
                </h2>
                
                <div className="flex flex-wrap gap-2">
                  {enhancedGroup.interests.map((interest, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full font-medium hover:scale-105 transition-transform cursor-default"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Ratings */}
            {enhancedGroup.ratings && enhancedGroup.ratings.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <StarIcon className="h-6 w-6 mr-2 text-yellow-500" />
                  Group Rating
                </h2>
                
                <div className="text-center">
                  <div className="text-5xl font-bold text-yellow-600 mb-2">
                    {enhancedGroup.averageRating?.toFixed(1) || '0.0'}
                  </div>
                  <div className="flex justify-center mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon 
                        key={star}
                        className={`h-6 w-6 ${star <= Math.round(enhancedGroup.averageRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    Based on {enhancedGroup.ratings.length} review{enhancedGroup.ratings.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            )}

            {/* Meeting Point */}
            {enhancedGroup.meetingPoint && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <MapPinIcon className="h-6 w-6 mr-2 text-red-500" />
                  Meeting Point
                </h2>
                
                <div className="space-y-3">
                  <p className="font-medium">{enhancedGroup.meetingPoint.location}</p>
                  {enhancedGroup.meetingPoint.dateTime && (
                    <p className="text-sm text-gray-600">
                      {new Date(enhancedGroup.meetingPoint.dateTime).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* REMOVED: Old action buttons section at the bottom */}
        
        {/* Floating Action Buttons Component */}
        <FloatingActionButtons />
      </div>
    </>
  );
};

// Helper Components
const InfoItem = ({ icon, label, value, highlight = false, badge = false }) => (
  <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
    <div className="flex items-center">
      <div className="mr-3">
        {icon}
      </div>
      <span className="text-gray-700">{label}</span>
    </div>
    {badge ? (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
        value === 'planning' ? 'bg-yellow-100 text-yellow-800' :
        value === 'confirmed' ? 'bg-green-100 text-green-800' :
        value === 'active' ? 'bg-blue-100 text-blue-800' :
        value === 'completed' ? 'bg-gray-100 text-gray-800' :
        'bg-red-100 text-red-800'
      }`}>
        {value}
      </span>
    ) : (
      <span className={`font-semibold ${highlight ? 'text-blue-600' : 'text-gray-900'}`}>
        {value}
      </span>
    )}
  </div>
);

const CrownIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 2a.75.75 0 01.75.75v.258l2.524.419a.75.75 0 01.586.643l.423 2.54 2.54.423a.75.75 0 01.643.586l.419 2.524h.258a.75.75 0 010 1.5h-.258l-.419 2.524a.75.75 0 01-.643.586l-2.54.423-.423 2.54a.75.75 0 01-.586.643L10.75 17.25v.258a.75.75 0 01-1.5 0v-.258l-2.524-.419a.75.75 0 01-.586-.643l-.423-2.54-2.54-.423a.75.75 0 01-.643-.586L2.75 11.25H2.5a.75.75 0 010-1.5h.258l.419-2.524a.75.75 0 01.643-.586l2.54-.423.423-2.54a.75.75 0 01.586-.643L9.25 2.75V2.5A.75.75 0 0110 2z" clipRule="evenodd" />
  </svg>
);

export default GroupDetailsPage;